import { Inject, Injectable, NgZone, OnDestroy } from '@angular/core'
import { LocalStorageService } from '../utils/local-storage.service'
import { LbHttpService } from '../lb-http.service'
import { ConfirmDialogComponent } from '../../dialog/confirm-dialog/confirm-dialog.component'
import { MatDialog } from '@angular/material/dialog'
import { LoadingService } from '../utils/loading.service'
import { Observable, Subject, throwError } from 'rxjs'
import { EventSourcePolyfill, NativeEventSource } from 'event-source-polyfill'
import moment from 'moment-timezone'
import { ApplicationsService } from '../applications.service'
import { ConfigService } from '../config.service'
import { TimeslotsService } from './timeslots.service'
import { ErrorService } from '../utils/error.service'
import { fromPromise } from 'rxjs/internal-compatibility'
import { catchError, first, mergeMap, retryWhen, switchMap, timeout } from 'rxjs/operators'
import { HttpClient } from '@angular/common/http'
import { L10N_LOCALE, L10nLocale } from 'angular-l10n'
import { LbTranslationService, sortTickets, sortLateTickets, sorInProgressTickets, sortDoneTickets, generateFilterFunctionFromPseudoCodeForArray, generateSortFunctionFromPseudoCode } from '@affluences/lb-utils-front'
import { QueueConfigService } from './queue-config.service'
import {DialogSelectEndpointComponent} from '../../dialog/dialog-select-endpoint/dialog-select-endpoint.component';

const EventSource = NativeEventSource || EventSourcePolyfill
let currentsTicketsSse

const lateStates = [ 300, 400 ]
const doneStates = [ 600, 700 ]
const inProgressState = 500
const calledState = 200

@Injectable( {
    providedIn: 'root'
} )
export class TicketService implements OnDestroy {

    private ticketLoaded: boolean = false
    private tickets: any = []
    private initialDoneTicket: any = []
    private ticketSubscription: any
    private ticketSseRetry: number = 0
    private maxTicketDisplay: number = 30
    private filter: any = null
    private sort: any = null
    private currentDateTicketData = null
    private currentEndpointId = null
    private onlyOwnTickets: {called: boolean, inProgress: boolean} = {called: false, inProgress: false}
    private doneTicketsLoaded = false
    private currentTicketsLoaded = false
    private reloadTicketTimeout = null

    private currentTicketsEvent: Subject<any> = new Subject<any>()
    private inProgressTicketsEvent: Subject<any> = new Subject<any>()
    private lateTicketsEvent: Subject<any> = new Subject<any>()
    private doneTicketsEvent: Subject<any> = new Subject<any>()
    private lastTicketUpdate: Subject<any> = new Subject<any>()

    public $currentTicketsEvent: Observable<any> = new Observable<any>()
    public $inProgressTicketsEvent: Observable<any> = new Observable<any>()
    public $lateTicketsEvent: Observable<any> = new Observable<any>()
    public $doneTicketsEvent: Observable<any> = new Observable<any>()
    public $lastTicketUpdate: Observable<any> = new Observable<any>()

    public currentTickets: any = []
    public inProgressTickets: any = []
    public lateTickets: any = []
    public doneTickets: any = []

    constructor(
        @Inject(L10N_LOCALE) public locale: L10nLocale,
        private zone: NgZone,
        private loader: LoadingService,
        public applicationsService: ApplicationsService,
        public queueConfigService: QueueConfigService,
        public configService: ConfigService,
        public localStorage: LocalStorageService,
        public lbHttpService: LbHttpService,
        private http: HttpClient,
        public dialog: MatDialog,
        public timeslotsService: TimeslotsService,
        private lbTranslationService: LbTranslationService,
        private errorService: ErrorService
    ) {
        this.$currentTicketsEvent = this.currentTicketsEvent.asObservable()
        this.$inProgressTicketsEvent = this.inProgressTicketsEvent.asObservable()
        this.$lateTicketsEvent = this.lateTicketsEvent.asObservable()
        this.$doneTicketsEvent = this.doneTicketsEvent.asObservable()
        this.$lastTicketUpdate = this.lastTicketUpdate.asObservable()
    }

    ngOnDestroy() {
        this.unsubscribe()
    }

    public unsubscribe() {
        this.ticketLoaded = false
        if (this.ticketSubscription) {
            this.ticketSubscription.unsubscribe()
        }
    }

    public setOnlyOwnTickets(onlyOwnTickets: {called: boolean, inProgress: boolean}): void {
        this.onlyOwnTickets = onlyOwnTickets
    }

    async loadTicketsIfNotLoaded(maxTicketDisplay, filter?, sort?) {
        this.maxTicketDisplay = maxTicketDisplay
        this.filter = filter ? filter : null
        this.sort = sort ? sort : null
        if (!this.ticketLoaded) {
            await this.initSubScribe()
        } else {
            this.reloadTicketToDisplay()
        }
    }

    async initSubScribe() {
        this.unsubscribe()
        this.ticketLoaded = true
        this.doneTicketsLoaded = false
        this.currentTicketsLoaded = false
        this.currentEndpointId = null
        this.loader.show()

        const fastLoading = !this.sort || (this.sort && JSON.stringify(this.sort) !== '{}')
        // GET INITIAL DONE TICKET
        this.loadAllDoneTickets(1, null, true)

        // GET INITIAL CURRENT TICKET
        this.loadAllCurrentTickets(1, null, fastLoading)

        // GET TICKETS WITH SSE
        this.ticketSubscription = (await this.getCurrentsTicketsSse()).subscribe(async (res) => {
            this.errorService.logDebug('Event on ticket SSE', res)
            this.ticketSseRetry = 0
            this.updateTicketFromEvent(res)
        }, async (error) => {
            this.errorService.logError('Error while getting current tickets by SSE', error, false)

            if (error === 'SSE_TEMPORARY_ERROR') {
                // GET INITIAL DONE TICKET
                this.loadAllDoneTickets(1, null, fastLoading)
                // GET INITIAL CURRENT TICKET
                this.loadAllCurrentTickets(1, null, fastLoading)

            } else if (error === 'SSE_CLOSED') {
                this.ticketSseRetry++

                if (this.ticketSseRetry < 5) {
                    return this.initSubScribe()
                } else {
                    this.unsubscribe()
                    this.errorService.reloadInterface()
                }
            }
        })
    }

    updateTicketFromEvent(event) {
        if (event.resource === 'TICKET') {
            if (event.actionCategory === 'ALL_DATA') {
                const now = Date.now()
                for (const k in event.data.tickets) {
                    this.findTicketAndDoAction(event.data.tickets[k], 'UPDATE', event.actionName, now)
                }

            } else if (event.actionCategory === 'CREATE' || event.actionCategory === 'UPDATE') {
                this.findTicketAndDoAction(event.data.ticket, 'UPDATE', event.actionName, event.time)
            }

            if (this.reloadTicketTimeout) clearTimeout(this.reloadTicketTimeout)
            this.reloadTicketTimeout = null

            this.reloadTicketTimeout = setTimeout(() => this.reloadTicketToDisplay(), 250)

        }
    }

    findTicketByTicketId(ticketId) {
        return this.tickets.find((el) => el.ticketId === ticketId)
    }

    findTicketByIdEvenIfDone( ticketId ) {
        let ticket = null
        ticket = this.tickets.find((el) => el.ticketId === ticketId)
        if (!ticket) ticket = this.doneTickets.find((el) => el.ticketId === ticketId)
        return ticket
    }

    findTicketAndDoAction(ticket, action, actionName, time) {
        let found = false

        for (const k in this.tickets) {
            if (this.tickets[k].ticketId === ticket.ticketId) {

                found = true

                if (!time || !this.tickets[k].lastUpdate || time > this.tickets[k].lastUpdate) {
                    if (action === 'UPDATE') {
                        if (this.tickets[k].state !== 600 && this.tickets[k].state !== 700) {
                            /*if ( actionName === 'TICKET_POSITION_UPDATE' ) {
                                this.tickets[k].timeline = ticket.timeline
                                this.tickets[k].ticketPosition = ticket.ticketPosition
                            } else {*/
                            this.tickets[k] = ticket
                            this.lastTicketUpdate.next(ticket)
                            // }
                        }
                        this.tickets[k].lastUpdate = time ? time : Date.now()

                    } else if (action === 'DELETE') {
                        this.tickets.splice(k, 1)
                        this.tickets[k].lastUpdate = time ? time : Date.now()
                    }
                }

                break
            }
        }

        if (!found && action === 'UPDATE') {
            ticket.lastUpdate = time ? time : Date.now()
            this.tickets.push(ticket)
        }

    }

    reloadTicketToDisplay() {

        if (!this.currentEndpointId || this.currentEndpointId === '') {
            this.currentEndpointId = this.localStorage.getSelectedEndpoint().endpointId
        }

        const startofDay = moment.tz(Date.now(), this.timeslotsService.getTimezone()).startOf('day').valueOf()
        // Delete the ticket of the past day if the interface stay opened on many days
        if (startofDay !== this.currentDateTicketData) {
            this.currentDateTicketData = startofDay

            const newTicketArray = []
            for (const t of this.tickets) {
                if (moment.tz(t.timeline.initialBookedFor, moment.ISO_8601, this.timeslotsService.getTimezone()).valueOf() >= startofDay) {
                    newTicketArray.push(JSON.parse(JSON.stringify(t)))
                }
            }

            this.tickets = JSON.parse(JSON.stringify(newTicketArray))

            const newInitialDoneTicketArray = []
            for (const t of this.initialDoneTicket) {
                if (moment.tz(t.timeline.initialBookedFor, moment.ISO_8601, this.timeslotsService.getTimezone()).valueOf() >= startofDay) {
                    newInitialDoneTicketArray.push(JSON.parse(JSON.stringify(t)))
                }
            }

            this.initialDoneTicket = JSON.parse(JSON.stringify(newInitialDoneTicketArray))
        }

        let currentTickets = []
        let inProgressTickets = []
        let lateTickets = []
        let doneTickets = []
        let currentPeopleNumber = 0
        let inProgressPeopleNumber = 0
        let latePeopleNumber = 0
        let donePeopleNumber = 0

        let initialDoneTicket = JSON.parse(JSON.stringify(this.initialDoneTicket))
        let tickets = JSON.parse(JSON.stringify(this.tickets))
        if (this.filter && JSON.stringify(this.filter) !== '{}') {
            initialDoneTicket = generateFilterFunctionFromPseudoCodeForArray(JSON.parse(JSON.stringify(initialDoneTicket)), this.filter, 'ticket')
            tickets = generateFilterFunctionFromPseudoCodeForArray(JSON.parse(JSON.stringify(tickets)), this.filter, 'ticket')
        }

        for (const k in initialDoneTicket) {
            doneTickets.push(JSON.parse(JSON.stringify(initialDoneTicket[k])))
            donePeopleNumber += initialDoneTicket[k].groupSize
        }

        for (const k in tickets) {
            if (lateStates.indexOf(tickets[k].state) >= 0) {
                lateTickets.push(JSON.parse(JSON.stringify(tickets[k])))
                latePeopleNumber += tickets[k].groupSize
            } else if (doneStates.indexOf(tickets[k].state) >= 0) {
                doneTickets.push(JSON.parse(JSON.stringify(tickets[k])))
                donePeopleNumber += tickets[k].groupSize
            } else if (tickets[k].state !== inProgressState) {
                // If the ticket is called he must be displayed for the client only if the endpoint who called the ticket is this endpoint
                if (tickets[k].state !== calledState || !this.onlyOwnTickets.called || tickets[k].endpoint.id === this.currentEndpointId) {
                    currentTickets.push(JSON.parse(JSON.stringify(tickets[k])))
                    currentPeopleNumber += tickets[k].groupSize
                }
            } else {
                // If the ticket is inProgress he must be displayed for the client only if the endpoint who called the ticket is this endpoint
                if (!this.onlyOwnTickets.inProgress || tickets[k].endpoint.id === this.currentEndpointId) {
                    inProgressTickets.push(JSON.parse(JSON.stringify(tickets[k])))
                    inProgressPeopleNumber += tickets[k].groupSize
                }
            }
        }

        currentTickets = currentTickets.sort(sortTickets)
        inProgressTickets = inProgressTickets.sort(sorInProgressTickets)
        lateTickets = lateTickets.sort(sortLateTickets)
        doneTickets = doneTickets.sort(sortDoneTickets)

        if (this.sort && JSON.stringify(this.sort) !== '{}') {
            currentTickets = generateSortFunctionFromPseudoCode(currentTickets, this.sort)
            inProgressTickets = generateSortFunctionFromPseudoCode(inProgressTickets, this.sort)
            lateTickets = generateSortFunctionFromPseudoCode(lateTickets, this.sort)
            doneTickets = generateSortFunctionFromPseudoCode(doneTickets, this.sort)
        }

        const currentTicketsNumber = currentTickets.length
        const inProgressTicketsNumber = inProgressTickets.length
        const lateTicketsNumber = lateTickets.length
        const doneTicketsNumber = doneTickets.length

        currentTickets = currentTickets.slice(0, this.maxTicketDisplay)
        inProgressTickets = inProgressTickets.slice(0, this.maxTicketDisplay)
        lateTickets = lateTickets.slice(0, this.maxTicketDisplay)
        doneTickets = doneTickets.slice(0, this.maxTicketDisplay)

        this.currentTickets = currentTickets
        this.currentTicketsEvent.next({number: currentTicketsNumber, tickets: this.currentTickets, people: currentPeopleNumber})

        this.inProgressTickets = inProgressTickets
        this.inProgressTicketsEvent.next({
            number: inProgressTicketsNumber,
            tickets: this.inProgressTickets,
            people: inProgressPeopleNumber
        })

        this.lateTickets = lateTickets
        this.lateTicketsEvent.next({number: lateTicketsNumber, tickets: this.lateTickets, people: latePeopleNumber})

        this.doneTickets = doneTickets
        this.doneTicketsEvent.next({number: doneTicketsNumber, tickets: this.doneTickets, people: donePeopleNumber})

    }

    searchTickets ( searchInputVal, useFilter = true ) {
        const perfectMatch = []
        const otherMatch = []


        let initialDoneTicket = JSON.parse( JSON.stringify( this.initialDoneTicket ) )
        let tickets = JSON.parse( JSON.stringify( this.tickets ) )
        if ( useFilter && this.filter && JSON.stringify( this.filter ) !== '{}' ) {
            initialDoneTicket = generateFilterFunctionFromPseudoCodeForArray( initialDoneTicket, this.filter, 'ticket' )
            tickets = generateFilterFunctionFromPseudoCodeForArray( tickets, this.filter, 'ticket' )
        }

        for (const k in tickets) {
            if (
                tickets[k].label === searchInputVal
                || (tickets[k].extendedAttributes && tickets[k].extendedAttributes.clientName === searchInputVal)
                || (tickets[k].extendedAttributes && tickets[k].extendedAttributes.name === searchInputVal)
            ) {
                perfectMatch.push(JSON.parse(JSON.stringify(tickets[k])))
            } else if (this.ticketMatchSearch(tickets[k], searchInputVal)) {
                otherMatch.push(JSON.parse(JSON.stringify(tickets[k])))
            }
        }

        for (const k in initialDoneTicket) {
            if (
                initialDoneTicket[k].label === searchInputVal
                || (initialDoneTicket[k].extendedAttributes && initialDoneTicket[k].extendedAttributes.clientName === searchInputVal)
                || (initialDoneTicket[k].extendedAttributes && initialDoneTicket[k].extendedAttributes.name === searchInputVal)
            ) {
                perfectMatch.push(JSON.parse(JSON.stringify(initialDoneTicket[k])))
            } else if (this.ticketMatchSearch(initialDoneTicket[k], searchInputVal)) {
                otherMatch.push(JSON.parse(JSON.stringify(initialDoneTicket[k])))
            }
        }

        let res = [...perfectMatch, ...otherMatch]
        return res.slice(0, this.maxTicketDisplay)
    }

    ticketMatchSearch(ticket, search) {
        if (search && search.length > 0) {
            const regexp = new RegExp('^' + this.escapeRegex(search), 'i')
            if (
                (ticket.extendedAttributes && ticket.extendedAttributes.clientName && ticket.extendedAttributes.clientName !== search && ticket.extendedAttributes.clientName.match(regexp))
                || (ticket.extendedAttributes && ticket.extendedAttributes.name && ticket.extendedAttributes.name !== search && ticket.extendedAttributes.name.match(regexp))
                || (ticket.label !== search && ticket.label.match(regexp))
            ) {
                return true
            } else {
                return false
            }
        } else {
            return true
        }
    }

    escapeRegex(str) {
        return str.replace(/([\+\*\-\)\(\[\]\.\&\^\?\!\$])/gi, '\\$1')
    }

    async loadAllDoneTickets(page, pageMax, fastLoading?) {
        this.errorService.logDebug('loadAllDoneTickets page = ' + page + ' , pageMax = ' + pageMax)

        if (page === 1) {
            this.initialDoneTicket = []
        }

        if ((fastLoading && page > 1) || (page > 1 && pageMax && page > pageMax)) {
            this.doneTicketsLoaded = true
            if (this.doneTicketsLoaded && this.currentTicketsLoaded) {
                this.loader.hide()
            }
        }

        if (page === 1 || page <= pageMax) {
            this.httpGetDoneTickets(page).subscribe(async (res) => {
                for (const k in res['tickets']) {
                    this.initialDoneTicket.push(res['tickets'][k])
                }
                this.reloadTicketToDisplay()

                const totalCount = res['totalCount'] > 0 ? res['totalCount'] - 1 : 0
                const pageSize = res['pageSize']

                await this.loadAllDoneTickets(res['page'] + 1, parseInt((totalCount / pageSize) + '', 10) + 1, fastLoading)
            }, async (error) => {
                this.errorService.logError('Error while getting done tickets, page = ' + page + ' , pageMax = ' + pageMax, error)
                this.errorService.unknownError()
            })
        }

    }

    async loadAllCurrentTickets(page, pageMax, fastLoading?, pageSize = 0) {
        this.errorService.logDebug('loadAllCurrentTickets page = ' + page + ' , pageMax = ' + pageMax)

        if (page === 1) {
            this.tickets = []
        }

        if ((fastLoading && page * pageSize > this.maxTicketDisplay) || (page > 1 && pageMax && page > pageMax)) {
            this.currentTicketsLoaded = true
            if (this.doneTicketsLoaded && this.currentTicketsLoaded) {
                this.loader.hide()
            }
            this.reloadTicketToDisplay()
        }

        if (page === 1 || page <= pageMax) {
            this.httpGetCurrentsTickets(page).subscribe(async (res) => {

                const now = Date.now()
                for (const k in res['tickets']) {
                    this.findTicketAndDoAction(res['tickets'][k], 'UPDATE', 'UPDATE', now)
                }

                const totalCount = res && res['totalCount'] > 0 ? res['totalCount'] - 1 : 0
                const pageSize = res && res['pageSize'] ? res['pageSize'] : 50

                await this.loadAllCurrentTickets(res['page'] + 1, parseInt((totalCount / pageSize) + '', 10) + 1, fastLoading, pageSize)
            }, async (error) => {
                this.errorService.logError('Error while getting all currents tickets, page = ' + page + ' , pageMax = ' + pageMax, error)
                this.errorService.unknownError()
            })
        }
    }

    async confirmSetTicketState(ticket, wantedState, showSetTicketStatePopup = true): Promise<any> {

        const promise = new Promise(async (resolve) => {
            if (ticket) {

                const data: any = {
                    text: '',
                    confirm: this.lbTranslationService.translateFromKey('endpoint_confirmDialog_confirm_yes', {lang: this.locale.language}),
                    cancel: this.lbTranslationService.translateFromKey('endpoint_confirmDialog_cancel_no', {lang: this.locale.language})
                }

                if (wantedState === 0) {
                    data.text = this.lbTranslationService.translateFromKey('endpoint_ticketService_popup_confirmSetState_text_booked', {lang: this.locale.language})
                } else if (wantedState === 50) {
                    data.text = this.lbTranslationService.translateFromKey('endpoint_ticketService_popup_confirmSetState_text_toConfirm', {lang: this.locale.language})
                } else if (wantedState === 100) {
                    data.text = this.lbTranslationService.translateFromKey('endpoint_ticketService_popup_confirmSetState_text_alerted', {lang: this.locale.language})
                } else if (wantedState === 200) {
                    data.text = this.lbTranslationService.translateFromKey('endpoint_ticketService_popup_confirmSetState_text_called', {lang: this.locale.language})
                } else if (wantedState === 300) {
                    data.text = this.lbTranslationService.translateFromKey('endpoint_ticketService_popup_confirmSetState_text_onHold', {lang: this.locale.language})
                } else if (wantedState === 400) {
                    data.text = this.lbTranslationService.translateFromKey('endpoint_ticketService_popup_confirmSetState_text_noShow', {lang: this.locale.language})
                } else if (wantedState === 500) {
                    data.text = this.lbTranslationService.translateFromKey('endpoint_ticketService_popup_confirmSetState_text_inProgress', {lang: this.locale.language})
                } else if (wantedState === 600) {
                    data.text = this.lbTranslationService.translateFromKey('endpoint_ticketService_popup_confirmSetState_text_cancel', {lang: this.locale.language})
                } else if (wantedState === 700) {
                    data.text = this.lbTranslationService.translateFromKey('endpoint_ticketService_popup_confirmSetState_text_done', {lang: this.locale.language})
                }

                if (showSetTicketStatePopup) {
                    const dialogData: any = {
                        width: '80vw',
                        data: data
                    }
                    const dialogRef = this.dialog.open(ConfirmDialogComponent, dialogData)

                    dialogRef.afterClosed().subscribe(async result => {
                        if (result && result.res) {
                            resolve(await this.setTicketState(ticket, wantedState))
                        } else {
                            resolve(false)
                        }
                    })
                } else {
                    resolve(await this.setTicketState(ticket, wantedState))
                }
            } else {
                resolve(false)
            }
        })

        return promise
    }

    callTicketOnEndpoint( ticket, endpoints ) {

        const promise = new Promise(async (resolve) => {
            const dialogData: any = {
                width: '80vw',
                data: endpoints
            }
            const dialogRef = this.dialog.open(DialogSelectEndpointComponent, dialogData)

            dialogRef.afterClosed().subscribe(async result => {
                if (result && result.res) {
                    resolve(await this.setTicketState(ticket, 200, result.endpointId))
                } else {
                    resolve(false)
                }
            })
        })
        return promise
    }

    async setTicketState(ticket, wantedState, endpointId?): Promise<any> {

        const promise = new Promise(async (resolve) => {
            if (ticket.state !== wantedState) {
                let ob = null
                let ticketEventActionName = ''
                switch (wantedState) {
                    case 0:
                        ob = this.httpSetTicketState(ticket.ticketId, {setStateTo: wantedState})
                        ticketEventActionName = 'TICKET_BOOKED'
                        break
                    case 50:
                        ob = this.httpSetTicketState(ticket.ticketId, {setStateTo: wantedState})
                        ticketEventActionName = 'TICKET_TO_CONFIRM'
                        break
                    case 100:
                        ob = this.httpSetTicketState(ticket.ticketId, {setStateTo: wantedState})
                        ticketEventActionName = 'TICKET_ALERTED'
                        break
                    case 200:
                        const tmpEndpointId1 = endpointId ? endpointId : this.localStorage.getSelectedEndpoint() && this.localStorage.getSelectedEndpoint().endpointId
                            ? this.localStorage.getSelectedEndpoint().endpointId : null
                        // TODO : show popup if endpoint ID not exist
                        ob = this.httpSetTicketState(ticket.ticketId, {setStateTo: wantedState, endpointId: tmpEndpointId1})
                        ticketEventActionName = 'TICKET_CALLED'
                        break
                    case 300:
                        ob = this.httpSetTicketState(ticket.ticketId, {setStateTo: wantedState})
                        ticketEventActionName = 'TICKET_ONHOLD'
                        break
                    case 400:
                        ob = this.httpSetTicketState(ticket.ticketId, {setStateTo: wantedState})
                        ticketEventActionName = 'TICKET_NOSHOW'
                        break
                    case 500:
                        const tmpEndpointId2 = this.localStorage.getSelectedEndpoint() && this.localStorage.getSelectedEndpoint().endpointId
                            ? this.localStorage.getSelectedEndpoint().endpointId : null
                        // TODO : show popup if endpoint ID not exist
                        ob = this.httpSetTicketState(ticket.ticketId, {setStateTo: wantedState, endpointId: tmpEndpointId2})
                        ticketEventActionName = 'TICKET_INPROGRESS'
                        break
                    case 600:
                        ob = this.httpSetTicketState(ticket.ticketId, {setStateTo: wantedState})
                        ticketEventActionName = 'TICKET_CANCELED'
                        break
                    case 700:
                        ob = this.httpSetTicketState(ticket.ticketId, {setStateTo: wantedState})
                        ticketEventActionName = 'TICKET_DONE'
                        break
                }

                if (ob) {
                    await this.loader.show()

                    try {
                        ob.subscribe(async (res) => {
                            this.errorService.logDebug('Set ticket state success', res)
                            const ticketEvent = {
                                resource: 'TICKET',
                                actionCategory: 'UPDATE',
                                actionName: ticketEventActionName,
                                time: Date.now(),
                                data: {ticket: res}
                            }
                            this.updateTicketFromEvent(ticketEvent)
                            await this.loader.hide()
                            resolve(true)
                        }, async (error) => {
                            this.errorService.logError('Error while setting ticket state or recall ticket', error)
                            resolve(false)

                            if (
                                error && error.error && error.error.error &&
                                (error.error.error === 'STATE_CHANGE_NOT_ALLOWED_ON_THE_QUEUE' || error.error.error === 'STATE_CHANGE_NOT_ALLOWED_FOR_THE_OPERATOR')
                            ) {
                                await this.loader.hide()
                                await this.initSubScribe()
                            } else {
                                await this.loader.hide()
                                this.errorService.unknownError()
                            }
                        })
                    } catch (error) {
                        this.errorService.logError('Error while setting ticket state or recall ticket global', error)
                        await this.loader.hide()
                        resolve(false)
                    }
                } else {
                    resolve(false)
                }
            } else {
                resolve(false)
            }
        })

        return promise
    }

    private async getCurrentsTicketsSse() {
        let url = this.configService.getCurrentsTicketsSse
        url = url.replace('{queueId}', this.localStorage.getSelectedQueue()['queueId'])
        const httpOptions = await this.lbHttpService.generateHeaderForLineberty(true, true, 'validation')
        httpOptions['params']['pageSize'] = 10

        if (currentsTicketsSse) {
            currentsTicketsSse.close()
        }

        return Observable.create((observer) => {
            currentsTicketsSse = new EventSourcePolyfill(this.lbHttpService.applyGetParamsToUrl(url, httpOptions.params), {
                headers: httpOptions.headers
            })
            currentsTicketsSse.onopen = () => {
                this.errorService.logDebug('Ticket SSE connected')
            }
            currentsTicketsSse.onmessage = (event) => {
                this.zone.run(() => observer.next(JSON.parse(event.data)))
            }
            currentsTicketsSse.onerror = (error) => {
                this.errorService.logError('Error on ticket sse', error, false)
                this.zone.run(() => observer.error('SSE_CLOSED'))
            }
        })
    }

    public httpSetTicketState(ticketId, params) {
        let url = this.configService.setTicketState
        url = url.replace('{ticketId}', ticketId)
        const body = params

        return fromPromise(this.lbHttpService.generateHeaderForLineberty(true, false, 'validation')).pipe(
            timeout(this.configService.defaultHttpTimeout),
            mergeMap((httpOptions: any) => {
                return this.http.put(url, body, httpOptions)
            }),
            first(),
            retryWhen(this.lbHttpService.genericRetryStrategy({})),
            catchError((error) => {
                this.errorService.logError('Error while setting ticket state', error)
                const errorName = error.error.error

                const data: any = {
                    width: '80vw',
                    data: {
                        text: '',
                        title: this.lbTranslationService.translateFromKey('endpoint_ticketService_popupErrorSetState_title', {lang: this.locale.language}),
                        confirm: this.lbTranslationService.translateFromKey('endpoint_confirmDialog_confirm_ok', {lang: this.locale.language})
                    }
                }

                switch (errorName) {
                    case 'TICKET_ID_NOT_FOUND':
                        data.data.text = this.lbTranslationService.translateFromKey('endpoint_ticketService_popupErrorSetState_ticketIdNotFound_text', {lang: this.locale.language})
                        break
                    case 'STATE_PARAM_MISSING':
                        data.data.text = this.lbTranslationService.translateFromKey('endpoint_ticketService_popupErrorSetState_stateParamMissing_text', {lang: this.locale.language})
                        break
                    case 'STATE_PARAM_BAD_VALUE':
                        data.data.text = this.lbTranslationService.translateFromKey('endpoint_ticketService_popupErrorSetState_stateParamBadValue_text', {lang: this.locale.language})
                        break
                    case 'PARAM_ENDPOINT_ID_MISSING':
                        data.data.text = this.lbTranslationService.translateFromKey('endpoint_ticketService_popupErrorSetState_paramEndpointIdMissing_text', {lang: this.locale.language})
                        break
                    case 'INVALID_ACCOUNT':
                        data.data.text = this.lbTranslationService.translateFromKey('endpoint_ticketService_popupErrorSetState_invalidAccount_text', {lang: this.locale.language})
                        break
                    case 'INVALID_ENDPOINT_ID_PARAM':
                        data.data.text = this.lbTranslationService.translateFromKey('endpoint_ticketService_popupErrorSetState_invalidEndpointIdParam_text', {lang: this.locale.language})
                        break
                    case 'HIDDEN_MODE_NOT_ALLOWED_FOR_THE_QUEUE':
                        data.data.text = this.lbTranslationService.translateFromKey('endpoint_ticketService_popupErrorSetState_hiddenModeNotAllowedForTheQueue_text', {lang: this.locale.language})
                        break
                    case 'STATE_CHANGE_NOT_ALLOWED_ON_THE_QUEUE':
                        data.data.text = this.lbTranslationService.translateFromKey('endpoint_ticketService_popupErrorSetState_stateChangeNotAllowedOnTheQueue_text', {lang: this.locale.language})
                        break
                    case 'STATE_CHANGE_NOT_ALLOWED_FOR_THE_OPERATOR':
                        data.data.text = this.lbTranslationService.translateFromKey('endpoint_ticketService_popupErrorSetState_stateChangeNotAllowedForTheOperator_text', {lang: this.locale.language})
                        break
                    default:
                        data.data.text = this.lbTranslationService.translateFromKey('endpoint_ticketService_popupErrorSetState_unknownError_text', {lang: this.locale.language})
                        break
                }

                this.dialog.open(ConfirmDialogComponent, data)
                return throwError(error)
            })
        )
    }

    public httpSetTicketExtendedAttributes(ticketId: string, params: {[key: string]: any}) {
        let url = this.configService.setTicketExtendedAttributes
        url = url.replace('{ticketId}', ticketId)
        const body = params

        return fromPromise(this.lbHttpService.generateHeaderForLineberty(true, false, 'validation')).pipe(
            timeout(this.configService.defaultHttpTimeout),
            mergeMap((httpOptions: any) => {
                return this.http.put(url, body, httpOptions)
            }),
            first(),
            retryWhen(this.lbHttpService.genericRetryStrategy({})),
            catchError((error) => {
                this.errorService.logError('Error while setting ticket extended attributes', error)
                const errorName = error.error.error

                const data: any = {
                    width: '80vw',
                    data: {
                        text: '',
                        confirm: this.lbTranslationService.translateFromKey('endpoint_confirmDialog_confirm_ok', {lang: this.locale.language})
                    }
                }

                switch (errorName) {
                    case 'TICKET_ID_NOT_FOUND':
                        data.data.text = this.lbTranslationService.translateFromKey('endpoint_ticketService_popupErrorSetState_ticketIdNotFound_text', {lang: this.locale.language})
                        break
                    case 'EMPTY_BODY':
                        data.data.text = this.lbTranslationService.translateFromKey('endpoint_ticketService_popupErrorSetState_stateParamMissing_text', {lang: this.locale.language})
                        break
                    case 'DUPLICATED_FIELD':
                        data.data.text = this.lbTranslationService.translateFromKey('endpoint_ticketService_popupErrorSetState_stateParamBadValue_text', {lang: this.locale.language})
                        break
                    case 'INVALID_FIELD_VALUE':
                        data.data.text = this.lbTranslationService.translateFromKey('endpoint_ticketService_popupErrorSetState_paramEndpointIdMissing_text', {lang: this.locale.language})
                        break
                    case 'INVALID_ACCOUNT':
                        data.data.text = this.lbTranslationService.translateFromKey('endpoint_ticketService_popupErrorSetState_invalidEndpointIdParam_text', {lang: this.locale.language})
                        break
                    case 'TICKET_STATE_NOT_ALLOW_THIS_ACTION':
                        data.data.text = this.lbTranslationService.translateFromKey('endpoint_ticketService_popupErrorSetState_hiddenModeNotAllowedForTheQueue_text', {lang: this.locale.language})
                        break
                    case 'UNAUTHORIZED_CHANGE_ON_THE_QUEUE':
                        data.data.text = this.lbTranslationService.translateFromKey('endpoint_ticketService_popupErrorSetState_stateChangeNotAllowedOnTheQueue_text', {lang: this.locale.language})
                        break
                    case 'UNAUTHORIZED_CHANGE_FOR_THE_OPERATOR':
                        data.data.text = this.lbTranslationService.translateFromKey('endpoint_ticketService_popupErrorSetState_stateChangeNotAllowedForTheOperator_text', {lang: this.locale.language})
                        break
                    default:
                        data.data.text = this.lbTranslationService.translateFromKey('endpoint_ticketService_popupErrorSetState_unknownError_text', {lang: this.locale.language})
                        break
                }

                this.dialog.open(ConfirmDialogComponent, data)
                return throwError(error)
            })
        )
    }

    public httpGetDoneTickets(page) {
        let url = this.configService.getDoneTickets
        url = url.replace('{queueId}', this.localStorage.getSelectedQueue()['queueId'])

        return fromPromise(this.lbHttpService.generateHeaderForLineberty(true, false, 'validation')).pipe(
            timeout(this.configService.bigtHttpTimeout),
            switchMap((httpOptions: any) => {
                httpOptions['params']['pageSize'] = 50
                httpOptions['params']['page'] = page
                return this.http.get(url, httpOptions)
            }),
            first(),
            retryWhen(this.lbHttpService.genericRetryStrategy({})),
            catchError((error) => {
                this.errorService.logError('Error while getting ended ticket', error)
                return throwError(error)
            })
        )
    }

    public httpGetCurrentsTickets(page) {
        let url = this.configService.getCurrentsTickets
        url = url.replace('{queueId}', this.localStorage.getSelectedQueue()['queueId'])

        return fromPromise(this.lbHttpService.generateHeaderForLineberty(true, false, 'validation')).pipe(
            timeout(this.configService.bigtHttpTimeout),
            switchMap((httpOptions: any) => {
                httpOptions['params']['pageSize'] = 50
                httpOptions['params']['page'] = page
                return this.http.get(url, httpOptions)
            }),
            first(),
            retryWhen(this.lbHttpService.genericRetryStrategy({})),
            catchError((error) => {
                this.errorService.logError('Error while getting currents tickets', error)
                return throwError(error)
            })
        )
    }

    public httpBookingTicket(obj) {
        let url
        let body: any
        return this.queueConfigService.getQueueVersion(obj.queueId).pipe(
            switchMap((version) => {
                if (version === 1) {
                    url = this.configService.bookingTicketV1
                } else {
                    url = this.configService.bookingTicketV2
                }
                url = url.replace('{queueId}', obj.queueId)
                this.localStorage.setCurrentTicket(null)
                this.localStorage.setUserBearer(null)

                const ticketType = obj.phone ? 'SMS' : 'VIRTUAL'
                body = {
                    appointmentTypeId: this.localStorage.getSelectedAppointmentTypeId(),
                    groupSize: this.localStorage.getSelectedGroupSize(),
                    lang: obj.lang ? obj.lang : this.configService.defaultLang,
                    userData: {
                        phone: obj.phone ? obj.phone : null,
                        mail: obj.mail ? obj.mail: null,
                        sendClientSms: !!obj.phone,
                        sendClientPush: false,
                        sendClientMail: !!obj.mail
                    },
                    extendedAttributes: {
                        ticketType: ticketType
                    },
                    position: {},
                    source: obj.source ? obj.source : this.configService.sourceBookingTicket,
                    ticketType: ticketType
                }

                if (obj.bookedFor) {
                    body.bookedFor = obj.bookedFor
                }
                if (obj.civility) {
                    body.extendedAttributes.clientCivility = obj.civility
                }
                if (obj.name) {
                    body.extendedAttributes.clientName = obj.name
                    body.userData.name = obj.name
                }
                if (typeof (obj.rgpd) !== 'undefined') {
                    body.extendedAttributes.rgpd = obj.rgpd
                }

                body.extendedAttributes = Object.assign(body.extendedAttributes, obj.extendedAttributes)

                return fromPromise(this.lbHttpService.generateHeaderForLineberty(true, false, 'booking'))
            }),
            timeout(this.configService.bigtHttpTimeout),
            switchMap((httpOptions: any) => {
                return this.http.post(url, body, httpOptions)
            }),
            first(),
            retryWhen(this.lbHttpService.genericRetryStrategy({})),
            catchError((error) => {
                this.errorService.logError('Error while booking ticket', error)

                const errorName = error.error.error

                const data: any = {
                    width: '80vw',
                    data: {
                        text: '',
                        confirm: this.lbTranslationService.translateFromKey('endpoint_confirmDialog_confirm_ok', {lang: this.locale.language})
                    }
                }

                switch (errorName) {
                    case 'CLIENT_TICKET_FOUND':
                        data.data.text = this.lbTranslationService.translateFromKey('endpoint_booking_popupError_clientTicketFound_text', {lang: this.locale.language})
                        break

                    case 'BOOKING_IS_CLOSED':
                        data.data.text = this.lbTranslationService.translateFromKey('endpoint_booking_popupError_queueIsClosed_text', {lang: this.locale.language})
                        break

                    case 'QUEUE_IS_CLOSED':
                        data.data.text = this.lbTranslationService.translateFromKey('endpoint_booking_popupError_queueIsClosed_text', {lang: this.locale.language})
                        break

                    case 'QUEUE_IS_ON_PAUSE':
                        data.data.text = this.lbTranslationService.translateFromKey('endpoint_booking_popupError_queueIsOnPause_text', {lang: this.locale.language})
                        break

                    case 'QUEUE_IS_FULL':
                        data.data.text = this.lbTranslationService.translateFromKey('endpoint_booking_popupError_queueIsFull_text', {lang: this.locale.language})
                        break

                    case 'INVALID_PHONE':
                        data.data.text = this.lbTranslationService.translateFromKey('endpoint_booking_popupError_invalidPhone_text', {lang: this.locale.language})
                        break

                    case 'INVALID_BOOKED_FOR':
                        data.data.text = this.lbTranslationService.translateFromKey('endpoint_booking_popupError_invalidBookedFor_text', {lang: this.locale.language})
                        break

                    case 'EXCEEDED_QUOTA':
                        data.data.text = this.lbTranslationService.translateFromKey('endpoint_booking_popupError_exceededQuota_text', {lang: this.locale.language})
                        break

                    default:
                        data.data.text = this.lbTranslationService.translateFromKey('endpoint_booking_popupError_unknownError_text', {lang: this.locale.language})
                        break
                }

                this.dialog.open(ConfirmDialogComponent, data)
                return throwError(error)
            })
        )
    }

    public httpFindTicket(label, today) {
        let url = this.configService.searchTickets
        url = url.replace('{queueId}', this.localStorage.getSelectedQueue()['queueId'])

        const body = {label, today}

        return fromPromise(this.lbHttpService.generateHeaderForLineberty(true, false, 'validation')).pipe(
            timeout(this.configService.defaultHttpTimeout),
            switchMap((httpOptions: any) => {
                return this.http.post(url, body, httpOptions)
            }),
            first(),
            retryWhen(this.lbHttpService.genericRetryStrategy({})),
            catchError((error) => {
                console.log('Error while looking for ticket with label %s', label)
                console.log(error)
                return throwError(error)
            })
        )
    }


    public httpGetTicketLife(ticketId) {
        let url = this.configService.getTicketLife
        url = url.replace('{ticketId}', ticketId)

        return fromPromise(this.lbHttpService.generateHeaderForLineberty(true, false, 'validation')).pipe(
            timeout(this.configService.defaultHttpTimeout),
            switchMap((httpOptions: any) => {
                return this.http.get(url, httpOptions)
            }),
            first(),
            retryWhen(this.lbHttpService.genericRetryStrategy({})),
            catchError((error) => {
                console.log('Error while looking for ticket life with ticketId %s', ticketId)
                console.log(error)
                return throwError(error)
            })
        )
    }

    public httpGetTicketSms(ticketId) {
        let url = this.configService.getTicketSms
        url = url.replace('{ticketId}', ticketId)

        return fromPromise(this.lbHttpService.generateHeaderForLineberty(true, false, 'validation')).pipe(
            timeout(this.configService.defaultHttpTimeout),
            switchMap((httpOptions: any) => {
                return this.http.get(url, httpOptions)
            }),
            first(),
            retryWhen(this.lbHttpService.genericRetryStrategy({})),
            catchError((error) => {
                console.log('Error while looking for ticket sms with ticketId %s', ticketId)
                console.log(error)
                return throwError(error)
            })
        )
    }
}
